import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CalendarDays, Check, ChevronDown, Copy, FileDown, FileSpreadsheet, Gauge, MessageSquareText,
  Presentation, Quote, RefreshCw, Search, Share2, Smile, Sparkles, Users
} from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { localeOf } from '../../assets/js/locale';
import SectionEyebrow from '../common/SectionEyebrow';
import Loader from '../common/Loader';
import DonutChart from '../common/DonutChart';
import MiniChart from '../common/MiniChart';
import LineChart from '../common/LineChart';
import WordCloud from '../common/WordCloud';
import WordNetwork from '../common/WordNetwork';
import { PresentationModal, ShareSurveyModal, parseParticipants, responsesText } from './DashboardModals';
import { downloadResponsesExcel } from '../../utils/surveyDownloads';
import useSurveyPdf from './useSurveyPdf';
import { clampRating } from '../common-questions/TRating';
import '../../assets/styles/SurveyDetail.css';

const API = process.env.REACT_APP_API_URL;
const AI = process.env.REACT_APP_AI_URL;
const TABS = ['resumen', 'preguntas', 'respuestas'];
const OPEN_TYPES = ['open', 'texto', 'abierta'];

const TONE_COLORS = { positive: '#2563EB', neutral: '#8C8983', negative: '#BA1A1A' };
const TONES = ['positive', 'neutral', 'negative'];

const EMOTIONS = {
  alegria: { label: 'homecharts_joy', color: '#F59E0B', tone: 'positive' },
  confianza: { label: 'homecharts_trust', color: '#0D9488', tone: 'positive' },
  sorpresa: { label: 'homecharts_surprise', color: '#C026D3', tone: 'neutral' },
  tristeza: { label: 'homecharts_sadness', color: '#2563EB', tone: 'negative' },
  miedo: { label: 'homecharts_fear', color: '#4F46E5', tone: 'negative' },
  ira: { label: 'homecharts_anger', color: '#BA1A1A', tone: 'negative' },
  disgusto: { label: 'homecharts_disgust', color: '#15803D', tone: 'negative' },
  asco: { label: 'homecharts_disgust', color: '#15803D', tone: 'negative' }
};

const plain = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const isOpenType = (tipo) => OPEN_TYPES.includes(plain(tipo));

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const surveyStatus = (survey) => {
  const { done } = parseParticipants(survey.participantes);
  const finished = new Date(survey.fecha_fin) < new Date();
  if (survey.download || (finished && done > 0)) return 'results_available';
  if (done === 0) return 'no_data';
  return 'open';
};

const formatNumber = (value, locale, digits = 1) =>
  Number(value).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: digits });

const HBarList = ({ items, format = (v) => v, emptyText }) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  if (!items.length) return <p className="tp-sd__empty-inline">{emptyText}</p>;
  return (
    <ul className="tp-sd__hbars">
      {items.map((item) => (
        <li key={item.label} className="tp-sd__hbar">
          <span className="tp-sd__hbar-label">{item.label}</span>
          <span className="tp-sd__hbar-track" aria-hidden="true">
            <span
              className="tp-sd__hbar-fill"
              style={{ width: `${item.value > 0 ? Math.max(3, (item.value / max) * 100) : 0}%`, background: item.color || 'var(--tp-violet)' }}
            />
          </span>
          <span className="tp-sd__hbar-value">{format(item.value)}</span>
        </li>
      ))}
    </ul>
  );
};

// Emociones de la IA (server: services/emociones_grupos.py): los 7 grupos, siempre y de mayor a menor,
// y debajo de cada uno las emociones concretas que lo forman. Los valores son % sobre todo lo escrito;
// lo que no tiene carga emocional va en una nota aparte, asi los 7 grupos + neutral suman 100.
const EmotionGroups = ({ groups, neutral, groupLabel, emotionLabel, emptyText, neutralText }) => {
  if (!groups || !groups.length) return <p className="tp-sd__empty-inline">{emptyText}</p>;
  const max = Math.max(...groups.map((g) => g.valor));
  return (
    <>
      <ul className="tp-sd__hbars">
        {groups.map((g) => (
          <li key={g.grupo} className="tp-sd__egroup">
            <div className="tp-sd__hbar">
              <span className="tp-sd__hbar-label">{groupLabel(g.grupo)}</span>
              <span className="tp-sd__hbar-track" aria-hidden="true">
                <span
                  className="tp-sd__hbar-fill"
                  style={{ width: `${g.valor > 0 ? Math.max(3, (g.valor / max) * 100) : 0}%`, background: (EMOTIONS[g.grupo] || {}).color || 'var(--tp-violet)' }}
                />
              </span>
              <span className="tp-sd__hbar-value">{Math.round(g.valor * 100)}%</span>
            </div>
            {g.detalle?.length > 0 && (
              <p className="tp-sd__egroup-detail">
                {g.detalle.map((d) => `${emotionLabel(d.emocion)} ${Math.round(d.valor * 100)}`).join(' · ')}
              </p>
            )}
          </li>
        ))}
      </ul>
      {neutral > 0 && <p className="tp-sd__egroups-neutral">{neutralText.replace('{pct}', Math.round(neutral * 100))}</p>}
    </>
  );
};

// Conclusión que escribe la IA en campos separados: titular, resumen, cita literal (opcional) y acciones.
// Por pregunta llega igual pero con una sola acción (`accion`).
const ConclusionBody = ({ data, actionsLabel, moreLabel, lessLabel, compact = false }) => {
  const actions = data.acciones || (data.accion ? [data.accion] : []);
  // El desarrollo (2 párrafos de la IA) va plegado: quien quiera leer más, lo despliega
  const paragraphs = (data.desarrollo || []).filter(Boolean);
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`tp-sd__concl${compact ? ' is-compact' : ''}`}>
      <p className="tp-sd__concl-title">{data.titular}</p>
      {data.resumen && <p className="tp-sd__concl-text">{data.resumen}</p>}
      {paragraphs.length > 0 && (
        <div className="tp-sd__concl-more">
          {expanded && paragraphs.map((p, i) => <p key={i} className="tp-sd__concl-text">{p}</p>)}
          <button
            type="button"
            className="tp-sd__concl-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? lessLabel : moreLabel}
            <ChevronDown size={14} strokeWidth={2.25} aria-hidden="true" className={expanded ? 'is-open' : undefined} />
          </button>
        </div>
      )}
      {data.cita && <blockquote className="tp-sd__concl-quote">{data.cita}</blockquote>}
      {actions.length > 0 && (
        <div className="tp-sd__concl-actions">
          {!compact && <p className="tp-sd__qlabel">{actionsLabel}</p>}
          <ul>
            {actions.map((a, i) => (
              <li key={i}><ArrowRight size={14} strokeWidth={2.25} aria-hidden="true" />{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Card = ({ title, hint, icon, children, className = '', action }) => (
  <section className={`tp-sd__card ${className}`.trim()}>
    {(title || action) && (
      <header className="tp-sd__card-head">
        <div>
          {title && <h2 className="tp-sd__card-title">{icon}{title}</h2>}
          {hint && <p className="tp-sd__card-hint">{hint}</p>}
        </div>
        {action}
      </header>
    )}
    {children}
  </section>
);

const StateBlock = ({ children, onRetry, retryLabel }) => (
  <div className="tp-sd__state">
    <p>{children}</p>
    {onRetry && (
      <button type="button" className="btn btn--outline btn--sm" onClick={onRetry}>
        <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
        {retryLabel}
      </button>
    )}
  </div>
);

const SurveyDetail = () => {
  const { id } = useParams();
  const surveyId = Number(id);
  const { t, language } = useLanguage();
  const { downloadPdf, pdfLoading, pdfUi } = useSurveyPdf();
  const locale = localeOf(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'resumen';

  const [survey, setSurvey] = useState(null);
  const [surveyState, setSurveyState] = useState('loading');
  const [results, setResults] = useState(null);
  const [resultsState, setResultsState] = useState('loading');
  const [responses, setResponses] = useState([]);
  const [responsesState, setResponsesState] = useState('loading');
  const [network, setNetwork] = useState(null);
  const [networkState, setNetworkState] = useState('loading');
  const [hume, setHume] = useState(null);
  const [humeState, setHumeState] = useState('idle');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [responseFilter, setResponseFilter] = useState('all');
  const [responseSearch, setResponseSearch] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  // Tono resaltado al pasar por el anillo o por la leyenda, como en la gráfica de la landing
  const [activeTone, setActiveTone] = useState(null);

  const emotionLabel = useCallback((name) => {
    const info = EMOTIONS[plain(name)];
    return info ? t(info.label) : name;
  }, [t]);

  // Emocion concreta de la IA (clave en ingles: 'annoyance'); los nombres estan en lang.json
  const fineEmotionLabel = useCallback((key) => {
    const textKey = `ai_emotion_${key}`;
    const label = t(textKey);
    return label === textKey ? key : label;
  }, [t]);

  const fromAdmin = searchParams.get('from') === 'admin';
  const backTo = fromAdmin ? '/admin' : '/dashboard';
  const backLabel = fromAdmin ? t('sd_back_admin') : t('sd_back');

  const loadSurvey = useCallback(async () => {
    setSurveyState('loading');
    try {
      // Por id: vale para el dueño y para un admin que llega desde Administrar
      const res = await fetch(`${API}/encuestas/${surveyId}`, { headers: authHeaders() });
      if (res.status === 404 || res.status === 403) {
        setSurvey(null);
        setSurveyState('missing');
        return;
      }
      if (!res.ok) throw new Error(res.status);
      setSurvey(await res.json());
      setSurveyState('ready');
    } catch (error) {
      console.error('Error al cargar el diagnóstico:', error);
      setSurveyState('error');
    }
  }, [surveyId]);

  const loadResults = useCallback(async () => {
    setResultsState('loading');
    try {
      const res = await fetch(`${API}/enviar-id-encuesta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ id_encuesta: surveyId, max_words: 20, conexiones_max_words: 10, lang: language })
      });
      if (!res.ok) throw new Error(res.status);
      setResults(await res.json());
      setResultsState('ready');
    } catch (error) {
      console.error('Error al cargar los resultados:', error);
      setResultsState('error');
    }
  }, [surveyId, language]);

  const loadResponses = useCallback(async () => {
    setResponsesState('loading');
    try {
      const res = await fetch(`${API}/api/responses/${surveyId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setResponses(Array.isArray(data) ? data : []);
      setResponsesState('ready');
    } catch (error) {
      console.error('Error al cargar las respuestas:', error);
      setResponsesState('error');
    }
  }, [surveyId]);

  const loadNetwork = useCallback(async () => {
    setNetworkState('loading');
    try {
      const res = await fetch(`${API}/api/encuestas/${surveyId}/red-palabras?max_words=30`, { headers: authHeaders() });
      if (!res.ok) throw new Error(res.status);
      setNetwork(await res.json());
      setNetworkState('ready');
    } catch (error) {
      console.error('Error al cargar la red de palabras:', error);
      setNetworkState('error');
    }
  }, [surveyId]);

  // El análisis de emociones puede tardar minutos: mientras el servidor responde
  // "procesando", se vuelve a preguntar cada pocos segundos.
  const humeRetryRef = useRef(null);
  useEffect(() => () => clearTimeout(humeRetryRef.current), []);

  const loadHume = useCallback(async () => {
    clearTimeout(humeRetryRef.current);
    setHumeState((current) => (current === 'waiting' ? 'waiting' : 'loading'));
    try {
      const res = await fetch(`${AI}/api/analisis-emociones-hume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ id_encuesta: surveyId })
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      if (data.estado === 'procesando') {
        setHumeState('waiting');
        humeRetryRef.current = setTimeout(loadHume, Math.max(3, Number(data.reintentar_en) || 10) * 1000);
        return;
      }
      setHume(data);
      setHumeState('ready');
    } catch (error) {
      console.error('Error en el análisis de emociones:', error);
      setHumeState('error');
    }
  }, [surveyId]);

  useEffect(() => {
    loadSurvey();
    loadResponses();
    loadNetwork();
  }, [loadSurvey, loadResponses, loadNetwork]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Las emociones de la IA salen en el Resumen y en cada pregunta: se piden al abrir la pagina.
  // Normalmente ya estan hechas (precalculo en segundo plano) y llegan de cache al momento.
  useEffect(() => {
    if (humeState === 'idle') loadHume();
  }, [humeState, loadHume]);

  useEffect(() => {
    if (!selectedQuestion && responses.length) setSelectedQuestion(responses[0].id_pregunta);
  }, [responses, selectedQuestion]);

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'resumen') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const chart = results?.chartData || {};

  // Emociones y tono de toda la encuesta: los de la IA (resumen_emociones), igual que el PDF
  const aiSummary = hume?.resumen_emociones || null;
  const aiGroups = aiSummary?.grupos || [];

  const toneTotals = {
    positive: aiSummary?.tono?.positivo || 0,
    neutral: aiSummary?.tono?.neutral || 0,
    negative: aiSummary?.tono?.negativo || 0
  };
  const toneSum = toneTotals.positive + toneTotals.neutral + toneTotals.negative;
  const pct = (value, total) => (total ? Math.round((value / total) * 100) : 0);

  // Los grupos ya vienen de mayor a menor; si todo es neutral no hay emocion principal
  const emotionSum = aiGroups.reduce((sum, g) => sum + g.valor, 0);
  const topGroup = aiGroups[0] && aiGroups[0].valor > 0 ? aiGroups[0] : null;
  const topEmotion = topGroup
    ? { label: emotionLabel(topGroup.grupo), value: topGroup.valor, color: (EMOTIONS[topGroup.grupo] || {}).color }
    : null;

  // Mientras la IA no ha terminado (o si falla), lo que va en lugar de sus graficos
  const aiPending = humeState === 'idle' || humeState === 'loading' || humeState === 'waiting';
  const renderAiStatus = () => {
    if (humeState === 'error') return <StateBlock onRetry={loadHume} retryLabel={t('sd_retry')}>{t('sd_q_ai_error')}</StateBlock>;
    if (aiPending) {
      return (
        <div className="tp-sd__ai-loading">
          <Loader />
          <span>{t(humeState === 'waiting' ? 'sd_q_ai_waiting' : 'sd_q_ai_loading')}</span>
        </div>
      );
    }
    return null;
  };

  // Cada pregunta con su escala: si todas usan la misma, la media tal cual (4,2 / 5); si no, pasadas a 0-10
  const satisfaction = useMemo(() => {
    const items = (chart.satisfactionData || []).filter((s) => Number.isFinite(Number(s.avg)) && s.max != null);
    if (!items.length) return null;
    const sameScale = new Set(items.map((s) => `${s.min}-${s.max}`)).size === 1;
    const mean = (key) => items.reduce((sum, s) => sum + Number(s[key]), 0) / items.length;
    return sameScale
      ? { value: mean('avg'), max: items[0].max, mixed: false }
      : { value: mean('score10'), max: 10, mixed: true };
  }, [chart.satisfactionData]);

  const trend = useMemo(() => {
    const points = (chart.trendsData || []).filter((p) => p && p.date);
    if (points.length < 2) return null;
    return {
      categories: points.map((p) => new Date(p.date).toLocaleDateString(locale, { day: 'numeric', month: 'short' })),
      series: [{ label: t('sd_trend_series'), color: '#7C3AED', values: points.map((p) => p.count) }]
    };
  }, [chart.trendsData, locale, t]);

  const analysisById = useMemo(() => new Map((results?.analisis_individual || []).map((a) => [a.id_pregunta, a])), [results]);
  const humeById = useMemo(() => new Map((hume?.resultados || []).map((r) => [r.id_pregunta, r])), [hume]);

  const participants = survey ? parseParticipants(survey.participantes) : { done: 0, invited: 0 };
  const status = survey ? surveyStatus(survey) : null;
  const dateRange = survey
    ? `${new Date(survey.fecha_inicio).toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${new Date(survey.fecha_fin).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : '';

  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(String(survey.pin));
      setCopiedPin(true);
      setTimeout(() => setCopiedPin(false), 1600);
    } catch (error) {
      console.error('No se pudo copiar el PIN:', error);
    }
  };

  const handleExcel = () => {
    try {
      downloadResponsesExcel({ responses, surveyTitle: survey?.titulo, t });
    } catch (error) {
      console.error('Error al generar el Excel:', error);
    }
  };

  const responsesLabel = (count) =>
    t(count === 1 ? 'dash_responses_one' : 'dash_responses_count').replace('{count}', count);

  const typeLabel = (tipo) => {
    if (isOpenType(tipo)) return t('question_type_open');
    if (plain(tipo) === 'closed') return t('question_type_closed');
    return t('dash_question_type_rating');
  };

  const typeClass = (tipo) => (isOpenType(tipo) ? 'open' : plain(tipo) === 'closed' ? 'closed' : 'rating');

  if (surveyState === 'loading') {
    return <div className="tp-sd"><Loader /></div>;
  }

  if (surveyState !== 'ready') {
    return (
      <div className="tp-sd">
        <Link to={backTo} className="tp-sd__back"><ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />{backLabel}</Link>
        <StateBlock onRetry={surveyState === 'error' ? loadSurvey : undefined} retryLabel={t('sd_retry')}>
          {surveyState === 'missing' ? t('sd_not_found') : t('sd_load_error')}
        </StateBlock>
      </div>
    );
  }

  const renderSummary = () => {
    if (resultsState === 'loading') return <div className="tp-sd__loading"><Loader /><p>{t('sd_loading_results')}</p></div>;
    if (resultsState === 'error') return <StateBlock onRetry={loadResults} retryLabel={t('sd_retry')}>{t('sd_load_error')}</StateBlock>;

    const structured = results?.conclusion_estructurada?.titular ? results.conclusion_estructurada : null;
    // Sin estructura solo llega el aviso de "sin respuestas abiertas"
    const conclusion = results?.conclusion || null;
    // Lo que suma y lo que resta lo escribe la IA junto a la conclusión, cada tema con una frase real
    const renderBalance = (items) => {
      if (!structured) {
        return <p className="tp-sd__empty-inline">{t(results?.conclusion_error ? 'sd_conclusion_unavailable' : 'sd_no_data')}</p>;
      }
      if (!items?.length) return <p className="tp-sd__empty-inline">{t('sd_balance_empty')}</p>;
      return (
        <ul className="tp-sd__themes">
          {items.map((item, i) => (
            <li key={i}>
              <p className="tp-sd__theme-title">{item.tema}</p>
              <p className="tp-sd__theme-quote">{item.frase}</p>
            </li>
          ))}
        </ul>
      );
    };

    return (
      <div className="tp-sd__summary">
        <div className="tp-sd__kpis">
          <div className="tp-sd__kpi">
            <span className="tp-sd__kpi-icon" aria-hidden="true"><Users size={18} strokeWidth={2} /></span>
            <p className="tp-sd__kpi-label">{t('sd_kpi_responses')}</p>
            <p className="tp-sd__kpi-value">
              {participants.done}
              {participants.invited > 0 && <span> {t('sd_kpi_of').replace('{total}', participants.invited)}</span>}
            </p>
            {participants.invited > 0 && (
              <span className="tp-sd__kpi-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, pct(participants.done, participants.invited))}%` }} />
              </span>
            )}
          </div>
          <div className="tp-sd__kpi">
            <span className="tp-sd__kpi-icon" aria-hidden="true"><Smile size={18} strokeWidth={2} /></span>
            <p className="tp-sd__kpi-label">{t('sd_kpi_emotion')}</p>
            {topEmotion ? (
              <>
                <p className="tp-sd__kpi-value">
                  <span className="tp-sd__dot" style={{ background: topEmotion.color }} aria-hidden="true" />
                  {topEmotion.label}
                </p>
                <p className="tp-sd__kpi-hint">{t('sd_kpi_emotion_share').replace('{pct}', pct(topEmotion.value, emotionSum))}</p>
              </>
            ) : <p className="tp-sd__kpi-value is-empty">{t(aiPending ? 'sd_ai_analyzing' : 'sd_no_data')}</p>}
          </div>
          <div className="tp-sd__kpi">
            <span className="tp-sd__kpi-icon" aria-hidden="true"><Gauge size={18} strokeWidth={2} /></span>
            <p className="tp-sd__kpi-label">{t('sd_kpi_satisfaction')}</p>
            {satisfaction !== null ? (
              <>
                <p className="tp-sd__kpi-value">{formatNumber(satisfaction.value, locale)}<span> / {satisfaction.max}</span></p>
                <p className="tp-sd__kpi-hint">{t(satisfaction.mixed ? 'sd_kpi_satisfaction_mixed' : 'sd_kpi_satisfaction_hint')}</p>
              </>
            ) : <p className="tp-sd__kpi-value is-empty">{t('sd_no_data')}</p>}
          </div>
          <div className="tp-sd__kpi">
            <span className="tp-sd__kpi-icon" aria-hidden="true"><Sparkles size={18} strokeWidth={2} /></span>
            <p className="tp-sd__kpi-label">{t('sd_kpi_tone')}</p>
            {toneSum ? (
              <>
                <p className="tp-sd__kpi-value">{pct(toneTotals.positive, toneSum)}%</p>
                <p className="tp-sd__kpi-hint">
                  {t('sd_kpi_tone_hint').replace('{neg}', pct(toneTotals.negative, toneSum)).replace('{neu}', pct(toneTotals.neutral, toneSum))}
                </p>
              </>
            ) : <p className="tp-sd__kpi-value is-empty">{t(aiPending ? 'sd_ai_analyzing' : 'sd_no_data')}</p>}
          </div>
        </div>

        {(structured || conclusion || results?.conclusion_error) && (
          <section className="tp-sd__conclusion">
            <SectionEyebrow>{t('sd_conclusion')}</SectionEyebrow>
            {structured
              ? (
                <ConclusionBody
                  data={structured}
                  actionsLabel={t('sd_conclusion_actions')}
                  moreLabel={t('sd_conclusion_more')}
                  lessLabel={t('sd_conclusion_less')}
                />
              )
              : conclusion
              ? <p className="tp-sd__conclusion-text">{conclusion}</p>
              // DeepSeek no pudo escribirla: mejor decirlo que enseñar un texto que no dice nada
              : <p className="tp-sd__conclusion-text is-unavailable">{t('sd_conclusion_unavailable')}</p>}
          </section>
        )}

        <div className="tp-sd__grid tp-sd__grid--2">
          <Card title={t('sd_emotions_title')} hint={t('sd_emotions_hint')}>
            {renderAiStatus() || (
              <EmotionGroups
                groups={aiGroups}
                neutral={aiSummary?.neutral}
                groupLabel={emotionLabel}
                emotionLabel={fineEmotionLabel}
                emptyText={t('sd_no_data')}
                neutralText={t('sd_emotions_neutral')}
              />
            )}
          </Card>
          <Card title={t('sd_tone_title')} hint={t('sd_tone_hint')}>
            {renderAiStatus() || (toneSum ? (
              <div className="tp-sd__tone">
                <DonutChart
                  data={TONES
                    .map((tone) => ({ label: t(`sd_${tone}`), value: toneTotals[tone], color: TONE_COLORS[tone] }))
                    .filter((d) => d.value > 0)}
                  size={180}
                  strokeWidth={24}
                  activeLabel={activeTone ? t(`sd_${activeTone}`) : null}
                  onSegmentHover={(segment) => setActiveTone(TONES.find((tone) => segment && t(`sd_${tone}`) === segment.label) || null)}
                  centerContent={(
                    // En el centro, el tramo resaltado; sin ninguno, el positivo
                    <div className="tp-sd__tone-center">
                      <strong>{pct(toneTotals[activeTone || 'positive'], toneSum)}%</strong>
                      <span>{t(`sd_${activeTone || 'positive'}`)}</span>
                    </div>
                  )}
                />
                <ul className="tp-sd__legend">
                  {TONES.map((tone) => (
                    <li key={tone}>
                      {/* Un botón y no un <li> con onClick: así también se llega con el tabulador y con Intro */}
                      <button
                        type="button"
                        className={activeTone === tone ? 'is-active' : undefined}
                        aria-pressed={activeTone === tone}
                        onMouseEnter={() => setActiveTone(tone)}
                        onMouseLeave={() => setActiveTone(null)}
                        onFocus={() => setActiveTone(tone)}
                        onBlur={() => setActiveTone(null)}
                        onClick={() => setActiveTone((current) => (current === tone ? null : tone))}
                      >
                        <span className="tp-sd__dot" style={{ background: TONE_COLORS[tone] }} aria-hidden="true" />
                        {t(`sd_${tone}`)}
                        <strong>{pct(toneTotals[tone], toneSum)}%</strong>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : <p className="tp-sd__empty-inline">{t('sd_no_data')}</p>)}
          </Card>
        </div>

        {trend && (
          <Card title={t('sd_trend_title')}>
            <LineChart series={trend.series} categories={trend.categories} height={170} />
          </Card>
        )}

        <div className="tp-sd__grid tp-sd__grid--words">
          <Card title={t('sd_words_title')}>
            {(chart.wordFrequencies || []).length
              ? <WordCloud words={chart.wordFrequencies} height={180} />
              : <p className="tp-sd__empty-inline">{t('sd_no_data')}</p>}
          </Card>
          <Card title={t('sd_words_balance')} hint={t('sd_balance_hint')}>
            <div className="tp-sd__balance">
              <div>
                <p className="tp-sd__balance-title is-plus">{t('sd_words_plus')}</p>
                {renderBalance(structured?.suma)}
              </div>
              <div>
                <p className="tp-sd__balance-title is-minus">{t('sd_words_minus')}</p>
                {renderBalance(structured?.resta)}
              </div>
            </div>
          </Card>
        </div>

        <Card title={t('sd_network_title')} hint={t('sd_network_desc')}>
          {networkState === 'loading' && <Loader />}
          {networkState === 'error' && <StateBlock onRetry={loadNetwork} retryLabel={t('sd_retry')}>{t('sd_load_error')}</StateBlock>}
          {networkState === 'ready' && (network?.nodes?.length > 1 ? (
            <WordNetwork
              nodes={network.nodes}
              edges={network.edges}
              labels={{
                graph: t('sd_network_graph'),
                hint: t('sd_network_hint'),
                appearsWith: t('sd_network_appears_with'),
                noLinks: t('sd_network_no_links'),
                node: (node) => t('sd_network_node').replace('{label}', node.label).replace('{count}', node.count),
                mentions: (node) => t('sd_network_mentions').replace('{count}', node.count).replace('{responses}', node.responses)
              }}
            />
          ) : <p className="tp-sd__empty-inline">{t('sd_network_empty')}</p>)}
        </Card>
      </div>
    );
  };

  const renderQuestion = (question) => {
    const answers = question.respuestas || [];
    const kind = typeClass(question.tipo);
    const analysis = analysisById.get(question.id_pregunta);
    const humeResult = humeById.get(question.id_pregunta);

    if (!answers.length) return <p className="tp-sd__empty-inline">{t('sd_q_no_answers')}</p>;

    if (kind === 'open') {
      // Todo lo de emociones sale de la IA: grupos con desglose y frases de ejemplo
      const aiQuestionGroups = humeResult?.grupos || [];
      // El resumen se monta aquí para que salga en el idioma de la web
      const aiTop = aiQuestionGroups
        .filter((g) => g.valor > 0)
        .slice(0, 3)
        .map((g) => `${emotionLabel(g.grupo)} ${Math.round(g.valor * 100)}%`)
        .join(', ');
      const humeCount = humeResult?.num_respuestas || 0;
      const humeSummary = !humeResult ? null
        : !humeCount ? t('sd_q_ai_no_texts')
          : aiTop ? t('sd_q_ai_summary').replace('{n}', humeCount).replace('{top}', aiTop)
            : t('sd_q_ai_summary_count').replace('{n}', humeCount);
      // Frases de ejemplo de los grupos con mas peso, en su mismo orden
      const examples = aiQuestionGroups
        .filter((g) => humeResult?.ejemplos?.[g.grupo]?.length)
        .slice(0, 4)
        .map((g) => [g.grupo, humeResult.ejemplos[g.grupo]]);

      return (
        <div className="tp-sd__qdetail">
          {/* Primero qué dicen (la conclusión): los porcentajes de emociones se malinterpretan sin ese contexto
              (un deseo de "recuperar la confianza" puntúa como Confianza) */}
          {(analysis?.conclusion?.titular || analysis?.conclusion_error) && (
            <div className="tp-sd__qsummary">
              <p className="tp-sd__qlabel">{t('sd_q_conclusion')}</p>
              {analysis.conclusion?.titular
                ? (
                  <ConclusionBody
                    data={analysis.conclusion}
                    moreLabel={t('sd_conclusion_more')}
                    lessLabel={t('sd_conclusion_less')}
                    compact
                  />
                )
                : <p className="tp-sd__conclusion-text is-unavailable">{t('sd_conclusion_unavailable')}</p>}
            </div>
          )}

          {/* Después las emociones que lo respaldan y las frases reales; con el precalculo ya suelen estar listas */}
          <div className="tp-sd__qblock tp-sd__ai">
            <p className="tp-sd__qlabel"><Sparkles size={14} strokeWidth={2} aria-hidden="true" />{t('sd_q_ai_emotions')}</p>
            {renderAiStatus() || (humeResult?.error
              ? <p className="tp-sd__empty-inline">{t('sd_q_ai_error')}</p>
              : (
                <>
                  {humeSummary && <p className="tp-sd__ai-summary">{humeSummary}</p>}
                  <EmotionGroups
                    groups={aiQuestionGroups}
                    neutral={humeResult?.neutral}
                    groupLabel={emotionLabel}
                    emotionLabel={fineEmotionLabel}
                    emptyText={t('sd_no_data')}
                    neutralText={t('sd_emotions_neutral')}
                  />
                  {examples.length > 0 && (
                    <div className="tp-sd__ai-examples">
                      <p className="tp-sd__qlabel">{t('sd_q_examples')}</p>
                      <div className="tp-sd__quotes">
                        {examples.map(([group, list]) => (
                          <figure key={group} className="tp-sd__quote" style={{ '--quote-color': (EMOTIONS[group] || {}).color || '#7C3AED' }}>
                            <figcaption><span className="tp-sd__dot" aria-hidden="true" />{emotionLabel(group)}</figcaption>
                            {list.slice(0, 2).map((sentence, i) => (
                              <blockquote key={i}><Quote size={14} strokeWidth={2} aria-hidden="true" />{sentence}</blockquote>
                            ))}
                          </figure>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ))}
          </div>

          <div className="tp-sd__qblock">
            <p className="tp-sd__qlabel">{t('sd_q_words')}</p>
            <HBarList
              items={(analysis?.palabras_frecuentes_data || []).slice(0, 8).map((w) => ({ label: w.word, value: w.count }))}
              emptyText={t('sd_no_data')}
            />
          </div>
        </div>
      );
    }

    const counts = new Map();
    answers.forEach((a) => {
      const key = String(a.respuesta).trim();
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });

    if (kind === 'rating') {
      const values = answers.map((a) => parseFloat(String(a.respuesta).replace(',', '.'))).filter(Number.isFinite);
      const average = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
      // La escala real de la pregunta; solo si no viene (preguntas antiguas) se deduce de las respuestas
      const hasScale = question.max_value !== null && question.max_value !== undefined && question.min_value !== null && question.min_value !== undefined;
      const scale = hasScale
        ? clampRating(question.min_value, question.max_value)
        : { min: Math.min(...values, 1) <= 0 ? 0 : 1, max: Math.max(...values, 0) <= 5 ? 5 : 10 };
      const scaleMax = scale.max;
      const scaleMin = scale.min;
      const histogram = [];
      for (let v = scaleMin; v <= scaleMax; v += 1) {
        const share = (v - scaleMin) / (scaleMax - scaleMin || 1);
        histogram.push({
          label: String(v),
          value: values.filter((x) => Math.round(x) === v).length,
          color: share >= 0.7 ? '#0D9488' : share >= 0.4 ? '#F59E0B' : '#BA1A1A'
        });
      }
      return (
        <div className="tp-sd__qdetail">
          <div className="tp-sd__rating">
            <div className="tp-sd__rating-avg">
              <p className="tp-sd__qlabel">{t('sd_q_average')}</p>
              <p className="tp-sd__rating-value">{average !== null ? formatNumber(average, locale) : '—'}<span> / {scaleMax}</span></p>
            </div>
            <div className="tp-sd__rating-chart">
              <p className="tp-sd__qlabel">{t('sd_q_distribution')}</p>
              <MiniChart data={histogram} showGrid axisMax={Math.max(4, Math.ceil(Math.max(...histogram.map((h) => h.value)) / 4) * 4)} axisStep={Math.max(1, Math.ceil(Math.max(...histogram.map((h) => h.value)) / 4))} height={160} />
            </div>
          </div>
        </div>
      );
    }

    const total = answers.length;
    return (
      <div className="tp-sd__qdetail">
        <p className="tp-sd__qlabel">{t('sd_q_distribution')}</p>
        <HBarList
          items={[...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }))}
          format={(v) => `${v} · ${pct(v, total)}%`}
          emptyText={t('sd_no_data')}
        />
      </div>
    );
  };

  const renderQuestions = () => {
    if (responsesState === 'loading') return <Loader />;
    if (responsesState === 'error') return <StateBlock onRetry={loadResponses} retryLabel={t('sd_retry')}>{t('sd_load_error')}</StateBlock>;
    if (!responses.length) return <StateBlock>{t('dash_questions_empty')}</StateBlock>;

    const currentIndex = Math.max(0, responses.findIndex((q) => q.id_pregunta === selectedQuestion));
    const current = responses[currentIndex];

    return (
      <div className="tp-sd__questions">
        <nav className="tp-sd__qnav" aria-label={t('sd_questions_label')}>
          <ol>
            {responses.map((q, index) => (
              <li key={q.id_pregunta}>
                <button
                  type="button"
                  className={`tp-sd__qnav-item ${index === currentIndex ? 'is-active' : ''}`.trim()}
                  aria-current={index === currentIndex ? 'true' : undefined}
                  onClick={() => setSelectedQuestion(q.id_pregunta)}
                >
                  <span className="tp-sd__qnav-num">{index + 1}</span>
                  <span className="tp-sd__qnav-text">{q.pregunta}</span>
                  <span className="tp-sd__qnav-meta">{typeLabel(q.tipo)} · {q.respuestas.length}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <section className="tp-sd__card tp-sd__qpanel">
          <header className="tp-sd__qhead">
            <p className="tp-sd__qlabel">{t('sd_question_n').replace('{n}', currentIndex + 1)}</p>
            <h2 className="tp-sd__qtitle">{current.pregunta}</h2>
            <div className="tp-sd__qtags">
              <span className={`tp-dmod__type is-${typeClass(current.tipo)}`}>{typeLabel(current.tipo)}</span>
              <span className="tp-sd__qcount"><MessageSquareText size={14} strokeWidth={2} aria-hidden="true" />{responsesLabel(current.respuestas.length)}</span>
            </div>
          </header>
          {renderQuestion(current)}
        </section>
      </div>
    );
  };

  const renderResponses = () => {
    if (responsesState === 'loading') return <Loader />;
    if (responsesState === 'error') return <StateBlock onRetry={loadResponses} retryLabel={t('sd_retry')}>{t('sd_load_error')}</StateBlock>;

    const needle = plain(responseSearch);
    const groups = responses
      .map((q, index) => ({ ...q, index }))
      .filter((q) => responseFilter === 'all' || q.id_pregunta === responseFilter)
      .map((q) => ({ ...q, shown: q.respuestas.filter((a) => !needle || plain(a.respuesta).includes(needle)) }))
      .filter((q) => q.shown.length);
    const totalAnswers = responses.reduce((sum, q) => sum + q.respuestas.length, 0);

    return (
      <div className="tp-sd__responses">
        <div className="tp-sd__resp-toolbar">
          <div className="tp-sd__chips" role="group" aria-label={t('sd_questions_label')}>
            <button type="button" className={`tp-sd__chip ${responseFilter === 'all' ? 'is-active' : ''}`.trim()} aria-pressed={responseFilter === 'all'} onClick={() => setResponseFilter('all')}>
              {t('sd_resp_all')}
            </button>
            {responses.map((q, index) => (
              <button
                key={q.id_pregunta}
                type="button"
                className={`tp-sd__chip ${responseFilter === q.id_pregunta ? 'is-active' : ''}`.trim()}
                aria-pressed={responseFilter === q.id_pregunta}
                title={q.pregunta}
                onClick={() => setResponseFilter(q.id_pregunta)}
              >
                {t('sd_question_short').replace('{n}', index + 1)}
              </button>
            ))}
          </div>
          <div className="tp-sd__resp-actions">
            <label className="tp-dash__search tp-sd__search">
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              <span className="tp-sr-only">{t('sd_resp_search')}</span>
              <input type="search" placeholder={t('sd_resp_search')} value={responseSearch} onChange={(e) => setResponseSearch(e.target.value)} />
            </label>
            <button type="button" className="btn btn--outline tp-sd__excel" onClick={handleExcel} disabled={!totalAnswers}>
              <FileSpreadsheet size={16} strokeWidth={2} aria-hidden="true" />
              {t('sd_resp_excel')}
            </button>
          </div>
        </div>

        {!groups.length && <StateBlock>{totalAnswers ? t('sd_resp_empty') : t('dash_no_responses')}</StateBlock>}

        {groups.map((q) => (
          <section key={q.id_pregunta} className="tp-sd__card tp-sd__resp-group">
            <header className="tp-sd__resp-head">
              <p className="tp-sd__qlabel">{t('sd_question_n').replace('{n}', q.index + 1)} · {typeLabel(q.tipo)}</p>
              <h2 className="tp-sd__qtitle">{q.pregunta}</h2>
              <p className="tp-sd__resp-count">{responsesLabel(q.shown.length)}</p>
            </header>
            <ol className="tp-sd__answers">
              {q.shown.map((a, i) => (
                <li key={i} className="tp-sd__answer">{a.respuesta}</li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    );
  };

  return (
    <div className="tp-sd">
      <Link to={backTo} className="tp-sd__back">
        <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        {backLabel}
      </Link>

      <header className="tp-sd__head">
        <div className="tp-sd__head-main">
          <div className="tp-sd__title-row">
            <h1 className="tp-sd__title">{survey.titulo}</h1>
            <span className={`tp-dash__status is-${status}`}>{t(`dash_status_${status}`)}</span>
          </div>
          <div className="tp-sd__meta">
            <span className="tp-dash__pin">
              {t('dash_th_pin')} <strong>{survey.pin}</strong>
              <button type="button" className="tp-sd__copy" onClick={copyPin} aria-label={t('dash_copy_pin')} title={t('dash_copy_pin')}>
                {copiedPin ? <Check size={14} strokeWidth={2.5} aria-hidden="true" /> : <Copy size={14} strokeWidth={2} aria-hidden="true" />}
              </button>
            </span>
            <span><CalendarDays size={14} strokeWidth={2} aria-hidden="true" />{dateRange}</span>
            <span>
              <Users size={14} strokeWidth={2} aria-hidden="true" />
              {responsesText(t, participants.done, participants.invited)}
            </span>
          </div>
        </div>
        <div className="tp-sd__head-actions">
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setShareOpen(true)}>
            <Share2 size={15} strokeWidth={2} aria-hidden="true" />{t('dash_share')}
          </button>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setPresentOpen(true)}>
            <Presentation size={15} strokeWidth={2} aria-hidden="true" />{t('dash_present')}
          </button>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => downloadPdf(survey)} disabled={pdfLoading}>
            <FileDown size={15} strokeWidth={2} aria-hidden="true" />
            {t('sd_pdf')}
          </button>
        </div>
      </header>

      <div className="tp-sd__tabs" role="tablist" aria-label={survey.titulo}>
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`sd-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`sd-panel-${key}`}
            className={`tp-sd__tab ${tab === key ? 'is-active' : ''}`.trim()}
            onClick={() => setTab(key)}
          >
            {t(`sd_tab_${key}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`sd-panel-${tab}`} aria-labelledby={`sd-tab-${tab}`} className="tp-sd__panel">
        {tab === 'resumen' && renderSummary()}
        {tab === 'preguntas' && renderQuestions()}
        {tab === 'respuestas' && renderResponses()}
      </div>

      <ShareSurveyModal survey={shareOpen ? survey : null} onClose={() => setShareOpen(false)} />
      <PresentationModal survey={presentOpen ? survey : null} dateRange={dateRange} onClose={() => setPresentOpen(false)} />
      {pdfUi}
    </div>
  );
};

export default SurveyDetail;
